// ScrollFix.js - A component to add to both App.js and SVGComponent

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import audioService from "./AudioService";

// Ensure browser does not auto-restore scroll positions on route changes (critical for iOS Safari)
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  try {
    window.history.scrollRestoration = 'manual';
  } catch (e) {}
}

export function forceScrollToTop() {
  if (typeof window === 'undefined') return;

  if ('scrollRestoration' in window.history) {
    try {
      window.history.scrollRestoration = 'manual';
    } catch (e) {}
  }

  // Unlock body overflow so scrollTo actually works
  document.body.classList.remove('lock-scroll', 'transitioning-page');
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';

  try {
    window.scrollTo(0, 0);
  } catch (e) {}

  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  } catch (e) {}

  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;

  const targets = [
    document.getElementById('root'),
    document.querySelector('.app-container'),
    document.querySelector('.page-container'),
    document.querySelector('.homepage'),
    document.querySelector('.homepage-content'),
    document.querySelector('main'),
    document.getElementById('slider-container'),
    document.querySelector('.slider-container'),
    document.querySelector('.content-sections'),
    document.querySelector('.left-panel'),
    document.querySelector('.right-panel'),
    document.querySelector('.plantContainerleft'),
    document.querySelector('.plantContainer'),
    document.querySelector('.combined-section'),
    document.querySelector('.white-container'),
  ];

  targets.forEach((el) => {
    if (el) {
      el.scrollTop = 0;
      if (el.scrollTo) {
        try {
          el.scrollTo(0, 0);
        } catch (e) {}
      }
    }
  });
}

// Optimized ScrollToTop component that guarantees scroll reset on route changes
export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    forceScrollToTop();

    const rafId = requestAnimationFrame(() => {
      forceScrollToTop();
    });

    const t1 = setTimeout(forceScrollToTop, 50);
    const t2 = setTimeout(forceScrollToTop, 150);
    const t3 = setTimeout(forceScrollToTop, 300);
    const t4 = setTimeout(forceScrollToTop, 500);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [pathname]);

  return null;
};

let lastClickTime = 0;
let lastClickId = null;

export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  const isSmallScreen = window.innerWidth <= 820;
  const isCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const isFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  return isSmallScreen || (isCoarsePointer && !isFinePointer);
};

// SVGComponent Click Handler - Modified version with universal double click/tap detection
export const createScrollSafeClickHandler = (onSelectGroup, onHover, onDoubleClickNav) => {
  return (id) => async (e) => {
    if (e) {
      if (e.preventDefault && typeof e.preventDefault === 'function') e.preventDefault();
      if (e.stopPropagation && typeof e.stopPropagation === 'function') e.stopPropagation();
    }

    // On desktop: hover plays sound, single click navigates immediately.
    // On mobile: single tap plays audio preview, double tap (within 400ms) navigates.
    if (!isMobileDevice()) {
      if (onDoubleClickNav) {
        onDoubleClickNav(id)(e);
      }
      return;
    }

    const now = Date.now();
    const timeDiff = now - lastClickTime;

    // Detect double-click / double-tap (within 400ms on the same element)
    if (lastClickId === id && timeDiff < 400) {
      lastClickTime = 0;
      lastClickId = null;
      if (onDoubleClickNav) {
        onDoubleClickNav(id)(e);
      }
      return;
    }

    lastClickTime = now;
    lastClickId = id;

    const resetGroupState = (targetId) => {
      const isMatch = (prev) => {
        if (!prev) return true;
        const cleanPrev = String(prev).replace('-group', '');
        const cleanTarget = String(targetId).replace('-group', '');
        return cleanPrev === cleanTarget || prev === targetId;
      };
      if (onSelectGroup) {
        onSelectGroup((prev) => (isMatch(prev) ? null : prev));
      }
      if (onHover) {
        onHover((prev) => (isMatch(prev) ? null : prev));
      }
    };

    if (onSelectGroup) {
      onSelectGroup(id);
    }

    try {
      const control = await audioService.playElementSound(id, {
        loop: false,
        volume: 1,
        fadeIn: true,
        fadeInDuration: 3000,
        fadeOutDuration: 3000,
      });

      if (!control) {
        resetGroupState(id);
        return;
      }

      setTimeout(() => {
        const fadeOutDuration = 3000; // Fade out slowly over 3 seconds
        control?.stop(fadeOutDuration);

        // Wait for the fade-out to complete before resetting selection and hover states
        setTimeout(() => {
          resetGroupState(id);
        }, fadeOutDuration);
      }, 3000); // Wait 3 seconds before starting fade-out
    } catch (err) {
      console.error("Error playing sound:", err);
      resetGroupState(id);
    }
  };
};

// Lightweight DOM Inspection utility (no-op for performance)
export function inspectScrollableElements() {
  return { scrollableElements: [], fixedElements: [] };
}

// Add this CSS to your App.css or create a new file
export const scrollFixCSS = `
/* Ensure the main containers can scroll properly */
html, body {
  scroll-behavior: auto !important;
  overflow-x: hidden;
}

/* Prevent scroll during page transitions */
body.transitioning-page {
  overflow: hidden !important;
}

/* Ensure the app container takes full height */
#root, .app, main {
  min-height: 100vh;
  width: 100%;
}
`;